import { scaleLinear } from 'd3-scale';
import { parseApiDate } from '../../services/dayjs';
import { TOMATO_RED } from '../../styles/theme';
import {
  MARKET_TRENDS_LINE_COLOURS,
  MARKET_TREND_COLOURS,
  MarketTrendDataPointsResponse,
  MarketTrendPointFigure,
  PriceIndexLevel,
} from '../../types/marketTrends';
import { Property } from '../../types/property';
import { SoldPriceData } from '../../types/soldPriceData';
import { WebsiteStatisticsResponse } from '../../types/statistics';

export interface LineValue {
  key: string;
  value: number;
  empty?: boolean;
  hidden?: boolean;
  hideLabel?: boolean;
  axisLabel?: string;
  highlight?: boolean;
}

export interface LineData {
  key: string;
  color: string;
  data: LineValue[];
}

const LINE_COLORS: { [key: string]: string } = {
  detached: '#36A4DD',
  semiDetached: '#7DBF7C',
  apartments: '#823B8F',
  terraced: '#2A5185',
};

const LABEL_MAP: { [key: string]: string } = {
  detached: 'Detached',
  semiDetached: 'Semi Detached',
  apartments: 'Apartments',
  terraced: 'Terraced',
};

export const NI_HOUSE_PRICE_LINK = 'https://www.finance-ni.gov.uk/topics/statistics-and-research/ni-house-price-index';
export const ELECTORAL_AREA_LINK = 'https://www.ninis2.nisra.gov.uk/public/AreaProfiledea.aspx';

export const formatPropertyStats = (property: Property, maxPoints: number): LineData[] => {
  const lineData: LineData[] = [];

  if (property.stats && property.stats.history) {
    let totalUnpublished = 0;

    const data = property.stats.history
      .filter((history) => {
        if (!history.published) totalUnpublished += 1;
        return !(!history.published && totalUnpublished > 2);
      })
      .map((history) => {
        return {
          rawDate: history.date,
          key: parseApiDate(history.date).format('Do MMM'),
          empty: false,
          value: history.totalViews,
          hidden: !history.published,
          highlight: history.featured,
        };
      });

    const slicedData = data.slice(0, maxPoints);

    if (slicedData.length > 0 && slicedData.length < maxPoints) {
      const missingPoints = maxPoints - slicedData.length;
      const newestDate = slicedData[0].rawDate;

      for (let i = 1; i <= missingPoints; i += 1) {
        const nextDate = parseApiDate(newestDate).add(i, 'days');

        slicedData.unshift({
          rawDate: nextDate.toISOString(),
          key: nextDate.format('Do MMM'),
          value: 0,
          empty: true,
          hidden: false,
          highlight: false,
        });
      }
    }

    const finalData = slicedData.map(({ rawDate, ...rest }) => rest).reverse();

    lineData.push({
      key: '',
      color: TOMATO_RED,
      data: finalData,
    });
  }

  return lineData;
};

export const formatPrimeStats = (stats: WebsiteStatisticsResponse, maxLabels: number): LineData[] => {
  const lineData: LineData[] = [];

  if (stats && stats.points) {
    const totalPoints = stats.points.length;
    const labelCountTarget = Math.min(totalPoints, Math.max(1, maxLabels)) - 1;
    const labelStep = (totalPoints - 1) / labelCountTarget;
    const labelledPointIndexes: number[] = [0];

    for (let i = 1; i <= labelCountTarget - 1; i += 1) {
      const num = 0 + labelStep * i;
      labelledPointIndexes.push(Math.ceil(num));
    }

    labelledPointIndexes.push(totalPoints - 1);

    const data = stats.points.map((point, i) => {
      return {
        key: parseApiDate(point.date).format('Do MMM'),
        value: point.totalViews || 0,
        highlight: point.featured,
        boosted: point.bumped,
        hideLabel: !labelledPointIndexes.includes(i),
        empty: point.totalViews == null,
      };
    });

    lineData.push({
      key: '',
      color: TOMATO_RED,
      data,
    });
  }

  return lineData;
};

export const formatSoldPrices = (soldPriceData: SoldPriceData): LineData[] => {
  const lineData: LineData[] = Object.keys(soldPriceData.areaPriceData.salesPriceInArea).map((key) => {
    const soldPrices = soldPriceData.areaPriceData.salesPriceInArea[key];

    const data = soldPrices.map((r) => {
      return {
        key: r.year.toString(),
        value: r.price,
      };
    });

    return {
      key: LABEL_MAP[key],
      color: LINE_COLORS[key],
      data,
    };
  });

  return lineData.sort((a, b) => {
    return a.data[a.data.length - 1].value > b.data[b.data.length - 1].value ? -1 : 1;
  });
};

export const formatSoldVolume = (soldPriceData: SoldPriceData) => {
  const data = soldPriceData.areaPriceData.salesVolumeInArea.map((salesVolume) => {
    return {
      key: salesVolume.year.toString(),
      value: salesVolume.volume,
    };
  });

  return [
    {
      key: '',
      color: LINE_COLORS.terraced,
      data,
    },
  ];
};

export const moneyFormatter = (value: number) => {
  if (value >= 1000000) {
    return `£${value / 1000000}m`;
  }

  if (value > 0) {
    return `£${value / 1000}k`;
  }

  return `£0`;
};

const maxValueReducer = (lastValue: number, nextValue: number) => {
  return lastValue > nextValue ? lastValue : nextValue;
};

export const getMaxValue = (lines: LineData[]) => {
  const maxValues: number[] = [];

  lines.forEach((line) => {
    maxValues.push(line.data.map((r) => r.value).reduce(maxValueReducer, 0));
  });

  return maxValues.reduce(maxValueReducer, 0);
};

export const getXAxisLabels = (lines: LineData[]) => {
  return lines?.[0]?.data?.map((r) => (r.hideLabel ? null : r.axisLabel || r.key));
};

export const getYAxisLabels = (lines: LineData[], height: number) => {
  const xMaxValue = Math.max(9, getMaxValue(lines));

  return scaleLinear([0, xMaxValue * 1.15], [0, height])
    .ticks()
    .reverse();
};

export const formatPropertyAreaHistory = (
  data: MarketTrendDataPointsResponse[],
  selected: PriceIndexLevel[],
  maxPoints: number,
  defaultColour?: string,
): LineData[] => {
  const lineData: LineData[] = [];

  if (data?.length) {
    selected.forEach((level) => {
      data
        .filter((d) => d.level === level)
        .forEach((d) => {
          const ld = (Object.entries(d.points) as [string, MarketTrendPointFigure][]).map(([key, value]) => {
            const year = key?.slice(-4) || '';
            const quarter = key?.slice(0, 2) || '';
            const lv: LineValue = {
              key,
              value: value.value!,
              hideLabel: quarter !== 'Q1',
              axisLabel: year,
              hidden: !value.value,
            };
            return lv;
          });
          if (ld.length) {
            lineData.push({
              key: d.label || '',
              color: defaultColour || MARKET_TREND_COLOURS[level],
              data: ld.slice(-maxPoints),
            });
          }
        });
    });
  }

  return lineData;
};

export const formatMarketTrends = (
  data: MarketTrendDataPointsResponse[],
  selected: string[],
  maxPoints: number,
): LineData[] => {
  const lineData: LineData[] = [];

  if (data?.length) {
    selected.forEach((type) => {
      data
        .filter((d) => d.label === type)
        .forEach((d) => {
          const ld = (Object.entries(d.points) as [string, MarketTrendPointFigure][]).map(([key, value]) => {
            const year = key?.slice(-4) || '';
            const quarter = key?.slice(0, 2) || '';
            const lv: LineValue = {
              key,
              value: value.value!,
              hideLabel: quarter !== 'Q1',
              axisLabel: year,
              hidden: !value.value,
            };
            return lv;
          });
          if (ld.length) {
            lineData.push({
              key: d.label || '',
              color: MARKET_TRENDS_LINE_COLOURS[data.findIndex((dt) => dt.label === type)] || TOMATO_RED,
              data: ld.slice(-maxPoints),
            });
          }
        });
    });
  }

  return lineData;
};
